import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { CalendarCheck, ImagePlus, Loader2, MessageCircle, Package, Plus, Trash2, Wrench, X } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { useCompanyCurrency, useLabels, useWorkspace } from '@/contexts/WorkspaceContext';
import type { CatalogProductInput } from '@/services/whatsapp-platform.service';
import { catalogService, CatalogItem, CatalogItemInput, CatalogKind } from '@/services/catalog.service';
import { MEDIA_FOLDERS, uploadsService } from '@/services/uploads.service';
import { Segmented, Switch } from './shared';

const TAG_SUGGESTIONS: Record<'food' | 'technology' | 'general', string[]> = {
  food: ['Vegetarian', 'Vegan', 'Halal', 'Gluten-free', 'Spicy', 'Contains nuts', 'Dairy-free', 'Chef’s pick'],
  technology: ['Popular', 'New', 'Monthly', 'One-off', 'Remote', 'On-site'],
  general: ['Popular', 'New', 'Best seller', 'Limited'],
};
const MAX_IMAGE_MB = 5;

interface Props {
  open: boolean;
  item: CatalogItem | null;
  categories: string[];
  family: 'food' | 'technology' | null;
  canDelete: boolean;
  onClose: () => void;
  onSaved: (item: CatalogItem, created: boolean) => void;
  onDeleted: (itemUid: string) => void;
}

export default function CatalogItemEditor({ open, item, categories, family, canDelete, onClose, onSaved, onDeleted }: Props) {
  const navigate = useNavigate();
  const companyName = useWorkspace().info?.companyName ?? '';
  // A hidden (staging) draft in the WhatsApp catalog, pre-filled from the saved item; the operator adds the product link and publishes.
  const sendToWhatsapp = () => {
    if (!item) return;
    const prefill: Partial<CatalogProductInput> = {
      name: item.name,
      retailerId: item.sku || item.itemUid,
      description: item.description || item.name,
      brand: companyName,
      price: item.price ? String(item.price) : '',
      currency: item.currency ?? companyCurrency,
      availability: item.isAvailable ? 'in stock' : 'out of stock',
      visibility: 'staging',
      imageUrl: item.imageUrl ?? '',
    };
    onClose();
    navigate('/whatsapp/catalog', { state: { prefillProduct: prefill } });
  };
  const t = useLabels();
  const noun = t('catalog', 'singular');
  const companyCurrency = useCompanyCurrency();
  // null = not chosen yet → the server uses the company's currency.
  const [itemCurrency, setItemCurrency] = useState<string | null>(null);
  const currency = itemCurrency ?? companyCurrency;
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CatalogKind>('product');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [sku, setSku] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [duration, setDuration] = useState('');
  const [bookable, setBookable] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? '');
    setKind(item?.kind ?? (family === 'technology' ? 'service' : 'product'));
    setCategory(item?.category ?? '');
    setPrice(item?.price != null ? String(item.price) : '');
    setDescription(item?.description ?? '');
    setTags(item?.tags ?? []);
    setTagDraft('');
    setImageUrl(item?.imageUrl ?? '');
    setSku(item?.sku ?? '');
    setIsAvailable(item?.isAvailable ?? true);
    setDuration(item?.durationMinutes != null ? String(item.durationMinutes) : '');
    setBookable(item?.bookable ?? false);
    setItemCurrency(item?.currency ?? null);
    setErrors({});
  }, [open, item, family]);

  const suggestions = useMemo(() => {
    const pool = TAG_SUGGESTIONS[family ?? 'general'];
    return pool.filter((s) => !tags.some((x) => x.toLowerCase() === s.toLowerCase()));
  }, [family, tags]);

  const categoryHints = useMemo(() => {
    const q = category.trim().toLowerCase();
    return categories.filter((c) => c.toLowerCase() !== q && (!q || c.toLowerCase().includes(q))).slice(0, 8);
  }, [categories, category]);

  const addTag = (raw: string) => {
    const tag = raw.trim().replace(/,$/, '').trim();
    if (!tag) return;
    if (tag.length > 30) { toast.error('Keep tags under 30 characters'); return; }
    if (tags.length >= 20) { toast.error('Up to 20 tags'); return; }
    if (!tags.some((x) => x.toLowerCase() === tag.toLowerCase())) setTags([...tags, tag]);
    setTagDraft('');
  };

  const onPickImage = async (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Choose an image file (JPG, PNG or WebP).'); return; }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) { toast.error(`Choose an image smaller than ${MAX_IMAGE_MB} MB.`); return; }
    setUploading(true);
    try {
      const r = await uploadsService.imagekit(file, MEDIA_FOLDERS.catalog, 'catalog');
      setImageUrl(r.url);
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Give it a name';
    if (price.trim()) {
      const n = Number(price);
      if (!Number.isFinite(n) || n < 0) next.price = 'Enter a price of 0 or more';
      else if (!/^\d+(\.\d{1,2})?$/.test(price.trim())) next.price = 'Use at most 2 decimals';
    }
    if (kind === 'service' && duration.trim()) {
      const d = Number(duration);
      if (!Number.isInteger(d) || d < 5 || d > 1440) next.duration = 'Between 5 and 1440 minutes';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    const isService = kind === 'service';
    const payload: CatalogItemInput = {
      name: name.trim(),
      kind,
      category: category.trim(),
      price: price.trim() ? Number(price) : null,
      currency: itemCurrency ?? undefined,
      description: description.trim(),
      tags,
      imageUrl: imageUrl || '',
      sku: sku.trim(),
      isAvailable,
      durationMinutes: isService && duration.trim() ? Number(duration) : null,
      bookable: isService && bookable,
    };
    setSaving(true);
    try {
      const saved = item ? await catalogService.update(item.itemUid, payload) : await catalogService.create(payload);
      toast.success(item ? `${noun} updated` : `${noun} added`);
      onSaved(saved, !item);
    } catch (e: any) {
      if (!e?.response) toast.error('Could not save — check your connection');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    const extra = item.bookable ? ' It also stops appearing on your booking link.' : '';
    if (!window.confirm(`Delete "${item.name}"?${extra}`)) return;
    setDeleting(true);
    try {
      await catalogService.remove(item.itemUid);
      toast.success(`${noun} deleted`);
      onDeleted(item.itemUid);
    } catch (e: any) {
      if (!e?.response) toast.error('Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={item ? `Edit ${noun.toLowerCase()}` : `New ${noun.toLowerCase()}`} widthClass="max-w-2xl">
      <form
        onSubmit={(e) => { e.preventDefault(); void save(); }}
        className="space-y-1"
      >
        <div className="grid gap-5 sm:grid-cols-[180px_1fr]">
          <div>
            <div
              className={clsx(
                'group relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed transition-colors sm:w-[180px]',
                imageUrl ? 'border-transparent' : 'border-border bg-surface-alt hover:border-primary/50',
              )}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); void onPickImage(e.dataTransfer.files?.[0]); }}
            >
              {imageUrl ? (
                <>
                  <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 flex items-end justify-center gap-1.5 bg-gradient-to-t from-black/60 via-black/0 to-black/0 p-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    <button type="button" onClick={() => fileRef.current?.click()} className="rounded-lg bg-white/90 px-2.5 py-1 text-xs font-semibold text-neutral-900 hover:bg-white">Replace</button>
                    <button type="button" onClick={() => setImageUrl('')} aria-label="Remove photo" className="rounded-lg bg-white/90 p-1.5 text-neutral-900 hover:bg-white"><Trash2 size={13} /></button>
                  </div>
                </>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary"><ImagePlus size={20} /></span>
                  <span className="text-[12.5px] font-semibold text-text-primary">Add a photo</span>
                  <span className="text-2xs text-text-muted">Drop or click · up to {MAX_IMAGE_MB} MB</span>
                </button>
              )}
              {uploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-surface/80">
                  <Loader2 size={20} className="animate-spin text-primary" />
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => void onPickImage(e.target.files?.[0])} />
            <div className="mt-3 flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-text-primary">Available</div>
                <div className="text-2xs text-text-muted">{isAvailable ? 'Can be ordered' : 'Hidden from orders'}</div>
              </div>
              <Switch checked={isAvailable} onChange={setIsAvailable} label="Available" />
            </div>
          </div>

          <div className="min-w-0">
            <FormField label="Name" required error={errors.name}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={family === 'food' ? 'e.g. Chicken shawarma plate' : family === 'technology' ? 'e.g. Website maintenance' : 'Name'} maxLength={120} invalid={!!errors.name} autoFocus />
            </FormField>

            <FormField label="Type">
              <Segmented
                value={kind}
                onChange={(k) => { setKind(k); if (k === 'product') setBookable(false); }}
                className="w-full"
                options={[
                  { value: 'product', label: 'Product', icon: <Package size={14} /> },
                  { value: 'service', label: 'Service', icon: <Wrench size={14} /> },
                ]}
              />
            </FormField>

            <div className="grid gap-x-4 sm:grid-cols-2">
              <FormField label="Category" hint="Groups items on the page">
                <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder={family === 'food' ? 'e.g. Mains' : 'e.g. Design'} maxLength={60} />
                {categoryHints.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {categoryHints.map((c) => (
                      <button key={c} type="button" onClick={() => setCategory(c)} className="rounded-full bg-surface-alt px-2.5 py-1 text-2xs font-medium text-text-secondary hover:bg-primary-soft hover:text-primary">
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </FormField>
              <FormField label="Price" hint="Blank = free / on request" error={errors.price}>
                <Input
                  value={price}
                  onChange={(e) => setPrice(e.target.value.replace(',', '.'))}
                  inputMode="decimal"
                  placeholder="0.00"
                  invalid={!!errors.price}
                  iconLeft={<span className="text-[11px] font-semibold">{currency}</span>}
                  className="pl-12 tabular-nums"
                />
              </FormField>
            </div>
          </div>
        </div>

        <FormField label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} placeholder={family === 'food' ? 'Ingredients, portion, how it’s served…' : 'What’s included'} />
        </FormField>

        <FormField label="Tags" hint="Press Enter to add">
          <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-lg border border-border bg-surface px-2 py-1.5 focus-within:border-primary focus-within:ring-4 focus-within:ring-primary-soft">
            {tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2.5 py-0.5 text-xs font-medium text-primary">
                {tag}
                <button type="button" aria-label={`Remove ${tag}`} onClick={() => setTags(tags.filter((x) => x !== tag))} className="rounded-full hover:text-text-primary"><X size={11} /></button>
              </span>
            ))}
            <input
              value={tagDraft}
              onChange={(e) => { if (e.target.value.endsWith(',')) addTag(e.target.value); else setTagDraft(e.target.value); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(tagDraft); }
                if (e.key === 'Backspace' && !tagDraft && tags.length) setTags(tags.slice(0, -1));
              }}
              onBlur={() => addTag(tagDraft)}
              placeholder={tags.length ? '' : 'Add a tag'}
              className="min-w-[90px] flex-1 border-none bg-transparent px-1 text-sm text-text-primary outline-none placeholder:text-text-muted"
            />
          </div>
          {suggestions.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {suggestions.map((s) => (
                <button key={s} type="button" onClick={() => addTag(s)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-0.5 text-2xs font-medium text-text-muted hover:border-primary hover:text-primary">
                  <Plus size={10} /> {s}
                </button>
              ))}
            </div>
          )}
        </FormField>

        {kind === 'service' && (
          <div className={clsx('mb-4 rounded-2xl border p-4 transition-colors', bookable ? 'border-primary/40 bg-primary-soft/40' : 'border-border')}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"><CalendarCheck size={17} /></span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-text-primary">Customers can book this</div>
                  <div className="text-xs text-text-muted">Appears on your booking link.</div>
                </div>
              </div>
              <Switch checked={bookable} onChange={setBookable} label="Bookable" />
            </div>
            <div className="mt-4 grid gap-x-4 sm:grid-cols-2">
              <FormField label="Duration" hint="minutes" error={errors.duration}>
                <Input value={duration} onChange={(e) => setDuration(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder={bookable ? '30' : 'e.g. 60'} invalid={!!errors.duration} />
              </FormField>
              {bookable && (
                <p className="mb-4 self-end text-2xs leading-relaxed text-text-muted">
                  New bookable services open on your usual hours.{' '}
                  {item?.apptServiceUid ? (
                    <Link to={`/appointments/services/${item.apptServiceUid}/edit`} className="font-semibold text-primary hover:underline">Adjust hours</Link>
                  ) : 'You can adjust hours after saving.'}
                </p>
              )}
            </div>
          </div>
        )}

        <FormField label="SKU / code" hint="Optional · unique">
          <Input value={sku} onChange={(e) => setSku(e.target.value)} maxLength={60} placeholder="e.g. MN-014" className="font-mono text-[13px]" />
        </FormField>

        <div className="sticky bottom-0 -mx-4 -mb-4 flex flex-wrap items-center justify-between gap-2 border-t border-border bg-surface px-4 py-3 sm:-mx-6 sm:-mb-5 sm:px-6">
          <div>
            {item && canDelete && (
              <Button type="button" variant="ghost" size="sm" className="text-danger hover:text-danger" iconLeft={<Trash2 size={14} />} loading={deleting} onClick={() => void remove()}>
                Delete
              </Button>
            )}
            {item && (
              <Button type="button" variant="ghost" size="sm" iconLeft={<MessageCircle size={14} />} disabled={!item.price || item.price <= 0}
                title={item.price && item.price > 0 ? 'Opens a WhatsApp catalog draft filled from this item — nothing is published until you do' : 'WhatsApp catalog products need a price'}
                onClick={sendToWhatsapp}>
                WhatsApp catalog
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={saving} disabled={uploading}>{item ? 'Save changes' : `Add ${noun.toLowerCase()}`}</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
